import { useHttp } from '@/composable/service/useHttp';
import hopeHttp, { GetHopeForm, HopeContentType } from '@/services/http/hope.http';
import { computed, onMounted, ref } from 'vue';
import { Hope } from '@/domain/hope.entity';
import useAsyncState from '@/composable/service/useAsyncState';
import useHopeState from '@/composable/states/useHopeState';

export interface GetHope extends Hope {
  newEstatePriceText: string,
  oldEstatePriceText: string,
  newMansionPriceText: string,
  oldMansionPriceText: string,
  landPriceText: string,
  businessPriceText: string,
}

export function formatHopePrice(
  hopePrice: [string, string] | null | undefined,
  hopeUnit: [string, string] | null | undefined,
  hopeValue: [number, number] | null | undefined): string {
    if (!hopePrice) return '';
    if (!hopeUnit) return '';
    if (!hopeValue) return '';
  const formattedMin = hopeValue[0] == 0 ? '下限なし' : `${hopePrice[0]}${hopeUnit[0]}`;
  const formattedMax = hopeValue[1] >= 999999 ? '上限なし' : `${hopePrice[1]}${hopeUnit[1]}`;
  return `${formattedMin}～${formattedMax}`;
}

export default function useGetMyHopes() {

  const http = useHttp(hopeHttp.get, { singleton: true });
  const { hopes } = useHopeState();
  const hopeState = useAsyncState(http.request, { dataRef: hopes });

  function getHopeRequest(form: Omit<GetHopeForm, 'contentType'> = {}) {
    return hopeState.request({ contentType: HopeContentType.actual, ...form });
  }

  onMounted(async () => {
    await getHopeRequest({});
  });

  return {
    hopes: computed(() => (hopeState.data.value || []).map((item): GetHope => ({
      ...item,
      businessPriceText: formatHopePrice(
        item.businessPriceFormatted ? [item.businessPriceFormatted.min, item.businessPriceFormatted.max] : undefined,
        item.businessPriceUnit ? [item.businessPriceUnit.min, item.businessPriceUnit.max] : undefined,
        item.businessPrice ? [item.businessPrice.min, item.businessPrice.max] : undefined,
      ),
      landPriceText: formatHopePrice(
        item.landPriceFormatted ? [item.landPriceFormatted.min, item.landPriceFormatted.max] : undefined,
        item.landPriceUnit ? [item.landPriceUnit.min, item.landPriceUnit.max] : undefined,
        item.landPrice ? [item.landPrice.min, item.landPrice.max] : undefined,
      ),
      newEstatePriceText: formatHopePrice(
        item.newEstatePriceFormatted ? [item.newEstatePriceFormatted.min, item.newEstatePriceFormatted.max] : undefined,
        item.newEstatePriceUnit ? [item.newEstatePriceUnit.min, item.newEstatePriceUnit.max] : undefined,
        item.newEstatePrice ? [item.newEstatePrice.min, item.newEstatePrice.max] : undefined,
        ),
      newMansionPriceText: formatHopePrice(
        item.newMansionPriceFormatted ? [item.newMansionPriceFormatted.min, item.newMansionPriceFormatted.max] : undefined,
        item.newMansionPriceUnit ? [item.newMansionPriceUnit.min, item.newMansionPriceUnit.max] : undefined,
        item.newMansionPrice ? [item.newMansionPrice.min, item.newMansionPrice.max] : undefined,
        ),
      oldEstatePriceText: formatHopePrice(
        item.oldEstatePriceFormatted ? [item.oldEstatePriceFormatted.min, item.oldEstatePriceFormatted.max] : undefined,
        item.oldEstatePriceUnit ? [item.oldEstatePriceUnit.min, item.oldEstatePriceUnit.max] : undefined,
        item.oldEstatePrice ? [item.oldEstatePrice.min, item.oldEstatePrice.max] : undefined,
        ),
      oldMansionPriceText: formatHopePrice(
        item.oldMansionPriceFormatted ? [item.oldMansionPriceFormatted.min, item.oldMansionPriceFormatted.max] : undefined,
        item.oldMansionPriceUnit ? [item.oldMansionPriceUnit.min, item.oldMansionPriceUnit.max] : undefined,
        item.oldMansionPrice ? [item.oldMansionPrice.min, item.oldMansionPrice.max] : undefined,
        ),
    }))),
    getHope: getHopeRequest,
    hopeLoading: hopeState.loading,
    hopeError: hopeState.error,
  };
}
